import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Gift, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { Logo } from '../components/Logo';
import TurnstileWidget from '../components/TurnstileWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignupProviderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Turnstile CAPTCHA
  const [turnstileToken, setTurnstileToken] = useState(null);
  const handleTurnstileToken = useCallback((token) => setTurnstileToken(token), []);
  
  // Invite/referral state
  const [inviteCode, setInviteCode] = useState('');
  const [inviteValid, setInviteValid] = useState(false);
  const [inviterInfo, setInviterInfo] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  // Promo code state (Session 28 — explicit field in signup)
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState(null); // {valid:bool, message?:string}
  const [promoChecking, setPromoChecking] = useState(false);
  
  // Form state - simplified to essential fields only
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  // Check for invite code in URL on mount
  useEffect(() => {
    const code = searchParams.get('invite');
    if (code) {
      setInviteCode(code);
      validateInviteCode(code);
    }
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.trim().toUpperCase());
    }
    const promo = searchParams.get('promo');
    if (promo) {
      setPromoCode(promo.trim().toUpperCase());
    }
  }, [searchParams]);

  const checkPromo = async (code) => {
    const c = (code || '').trim().toUpperCase();
    if (!c) { setPromoStatus(null); return; }
    setPromoChecking(true);
    try {
      const res = await axios.post(`${API_URL}/api/promo-codes/validate`, { code: c });
      setPromoStatus({ valid: !!res.data?.valid, message: 'Promo code looks good — your account will be activated.' });
    } catch (err) {
      setPromoStatus({
        valid: false,
        message: err.response?.data?.detail || 'Invalid promo code — you can still sign up without one.',
      });
    } finally {
      setPromoChecking(false);
    }
  };

  const validateInviteCode = async (code) => {
    try {
      const res = await axios.get(`${API_URL}/api/invites/validate/${code}`);
      if (res.data.valid) {
        setInviteValid(true);
        setInviterInfo({
          name: res.data.inviterName,
          businessName: res.data.inviterBusinessName
        });
      }
    } catch (error) {
      console.error('Error validating invite:', error);
    }
  };

  // Generate slug from business name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // CAPTCHA check
    if (!turnstileToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    
    // Validation
    if (!name || !businessName || !email || !phone || !password) {
      toast.error('Please fill all required fields');
      return;
    }

    // Password strength validation
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (!/[A-Za-z]/.test(password)) {
      toast.error('Password must contain at least one letter');
      return;
    }
    
    if (!/[0-9]/.test(password)) {
      toast.error('Password must contain at least one number');
      return;
    }

    try {
      setLoading(true);
      
      // Generate slug from business name
      const slug = generateSlug(businessName);
      
      const signupData = {
        businessName,
        ownerName: name,
        phone,
        email,
        password,
        slug,
        // Default values - will be updated in onboarding
        city: '',
        state: '',
        serviceRadiusMiles: 50,
        bio: '',
        providerCategory: 'OTHER',
        inviteCode: inviteCode || null,
        referralCode: referralCode || null,
        // Account starts as PENDING - must complete onboarding
        accountStatus: 'PENDING',
        onboardingCompleted: false
      };

      const response = await axios.post(`${API_URL}/api/auth/signup-provider`, signupData, {
        headers: { 'X-Turnstile-Token': turnstileToken }
      });

      const newToken = response.data.token;

      // Session 28 — apply promo code (if entered) right after account creation.
      // Invalid promo: warn but still let user proceed (account stays PENDING and
      // user can apply a different code from inside the dashboard).
      const trimmedPromo = (promoCode || '').trim().toUpperCase();
      if (trimmedPromo) {
        try {
          const promoRes = await axios.post(
            `${API_URL}/api/promo-codes/apply`,
            { code: trimmedPromo },
            { headers: { Authorization: `Bearer ${newToken}` } }
          );
          if (promoRes.data?.lifetimeAccess) {
            toast.success('Promo applied — lifetime access activated!');
          } else if (promoRes.data?.trialDays) {
            toast.success(`Promo applied — ${promoRes.data.trialDays}-day trial activated!`);
          } else {
            toast.success('Promo code applied');
          }
        } catch (promoErr) {
          toast.warning(promoErr.response?.data?.detail || "Couldn't apply promo code — you can try again from your dashboard.");
        }
      }

      // Login with returned token
      await login(newToken);
      
      toast.success('Account created! Please check your email to verify your account.');
      
      // Redirect to onboarding wizard
      navigate('/onboarding');
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create account';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Za-z]/.test(password), text: 'At least one letter' },
    { met: /[0-9]/.test(password), text: 'At least one number' },
  ];

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Logo Header */}
        <div className="flex justify-center mb-8">
          <Logo variant="full" size="md" />
        </div>

        {/* Invite Banner */}
        {inviteValid && inviterInfo && (
          <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Gift className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-semibold text-primary">You've been invited!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>{inviterInfo.businessName}</strong> has invited you to join RanchWorks.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Referral Banner */}
        {referralCode && !inviteValid && (
          <div
            className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg"
            data-testid="referral-banner"
          >
            <div className="flex items-start gap-3">
              <Gift className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">
                  Referred — 60-day free trial
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  You're signing up with referral code{' '}
                  <code className="px-1.5 py-0.5 rounded bg-amber-100 font-mono text-xs">
                    {referralCode}
                  </code>
                  . Your account will be activated immediately with a free 60-day trial.
                </p>
              </div>
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Create Your Account</CardTitle>
            <CardDescription>
              Join RanchWorks and start receiving bookings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Your Name *</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                />
              </div>

              {/* Business Name */}
              <div className="space-y-2">
                <Label htmlFor="businessName">Business Name *</Label>
                <Input
                  id="businessName"
                  data-testid="business-name-input"
                  placeholder="Smith Farrier Services"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  This will be displayed on your public profile
                </p>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  autoComplete="tel"
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a secure password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {/* Password requirements */}
                <div className="space-y-1 mt-2">
                  {passwordRequirements.map((req) => (
                    <div key={req.text} className={`flex items-center gap-2 text-xs ${req.met ? 'text-green-600' : 'text-muted-foreground'}`}>
                      <CheckCircle className={`h-3 w-3 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                      {req.text}
                    </div>
                  ))}
                </div>
              </div>

              {/* CAPTCHA */}
              <TurnstileWidget onToken={handleTurnstileToken} className="flex justify-center" />

              {/* Promo Code (Session 28 — explicit field, optional) */}
              <div className="space-y-2 pt-2 border-t" data-testid="promo-section">
                <Label htmlFor="promoCode">Promo Code <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <div className="flex gap-2">
                  <Input
                    id="promoCode"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); }}
                    placeholder="e.g. LAUNCH2026"
                    data-testid="promo-code-input"
                    className="font-mono uppercase tracking-wider"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => checkPromo(promoCode)}
                    disabled={promoChecking || !promoCode.trim()}
                    data-testid="promo-check-btn"
                  >
                    {promoChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
                  </Button>
                </div>
                {promoStatus && (
                  <p
                    className={`text-xs ${promoStatus.valid ? 'text-green-600' : 'text-amber-700'}`}
                    data-testid="promo-status"
                  >
                    {promoStatus.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Have an early-access promo? Enter it here to skip the payment step. You can also activate later.
                </p>
              </div>

              {/* Submit */}
              <div className="pt-2">
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold"
                  disabled={loading || password.length < 8 || !turnstileToken}
                  data-testid="signup-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                By signing up, you agree to our Terms of Service and Privacy Policy
              </p>

              <div className="text-center text-sm text-muted-foreground pt-2">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-primary hover:underline font-medium"
                >
                  Sign In
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center"
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Back to Home
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
